// src/pages/SurveyTaker.tsx: the survey completion experience.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useProfile, Firm } from '../hooks/useProfile';
import { supabase } from '../services/supabase';
import { logActivity } from '../lib/activity';
import {
  C, PulseSurvey, PulseQuestion, PulseResponse, AnswerValue,
  normalizeQuestion, isAnswered, STRATEGY_OPTIONS, AUM_RANGES, REGION_OPTIONS,
} from '../lib/pulse';
import { card, inputStyle, labelStyle, btnPrimary, btnGold, btnGhost, Banner, LoadingScreen, QuestionInput } from '../lib/pulseUI';

type AnswerMap = Record<string, AnswerValue>;

// Build a pulse_answers row payload from a local answer value.
function buildPayload(responseId: string, q: PulseQuestion, v: AnswerValue) {
  const base = { response_id: responseId, question_id: q.id, answer_text: null as string | null, answer_number: null as number | null, answer_options: [] as string[], answer_matrix: {} as Record<string, string> };
  switch (q.question_type) {
    case 'multiple_choice':
    case 'free_text': base.answer_text = v.text ?? null; break;
    case 'rating':
    case 'nps': base.answer_number = v.number ?? null; break;
    case 'multi_select':
    case 'ranking': base.answer_options = v.options || []; break;
    case 'matrix':
    case 'grid': base.answer_matrix = v.matrix || {}; break;
  }
  return base;
}

// Best-effort demographic prefill from the user's firm record.
function deriveDemographics(firm: Firm | null): { strategy: string; aum: string; region: string } {
  if (!firm) return { strategy: '', aum: '', region: '' };
  const strategy = STRATEGY_OPTIONS.find(o => o.toLowerCase() === (firm.type || '').toLowerCase()) || '';
  const aumVal = firm.aum_exact || firm.aum_max || firm.aum_min || 0;
  let aum = '';
  if (aumVal > 0) {
    const m = aumVal / 1_000_000; // assume stored in raw currency
    aum = m < 100 ? 'Under $100M' : m < 500 ? '$100M–$500M' : m < 1000 ? '$500M–$1B' : m < 5000 ? '$1B–$5B' : m < 10000 ? '$5B–$10B' : 'Over $10B';
  }
  const hq = (firm.headquarters || '').toLowerCase();
  const regionMap: [string, string[]][] = [
    ['North America', ['united states', 'usa', 'u.s', 'new york', 'boston', 'chicago', 'san francisco', 'canada', 'toronto']],
    ['UK', ['united kingdom', 'london', 'uk', 'england', 'scotland']],
    ['Europe', ['germany', 'france', 'switzerland', 'zurich', 'geneva', 'paris', 'frankfurt', 'europe', 'netherlands', 'amsterdam', 'luxembourg', 'ireland', 'dublin', 'spain', 'italy']],
    ['Middle East', ['dubai', 'abu dhabi', 'uae', 'qatar', 'saudi', 'middle east', 'bahrain']],
    ['Asia-Pacific', ['singapore', 'hong kong', 'tokyo', 'japan', 'china', 'shanghai', 'australia', 'sydney', 'asia', 'india', 'mumbai']],
    ['Latin America', ['brazil', 'mexico', 'argentina', 'chile', 'sao paulo', 'latin']],
    ['Africa', ['south africa', 'johannesburg', 'cape town', 'nigeria', 'africa']],
  ];
  const region = regionMap.find(([, kws]) => kws.some(k => hq.includes(k)))?.[0] || '';
  return { strategy, aum, region };
}

export default function SurveyTaker() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { firm, loading: profileLoading } = useProfile();

  const [survey, setSurvey] = useState<PulseSurvey | null>(null);
  const [questions, setQuestions] = useState<PulseQuestion[]>([]);
  const [response, setResponse] = useState<PulseResponse | null>(null);
  const [answers, setAnswers] = useState<AnswerMap>({});
  const [demo, setDemo] = useState({ strategy: '', aum: '', region: '' });
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showMissing, setShowMissing] = useState(false);
  const [savedAt, setSavedAt] = useState<Date | null>(null);
  const [saving, setSaving] = useState(false);

  const dirty = useRef<Set<string>>(new Set());
  const answersRef = useRef<AnswerMap>({});
  const responseRef = useRef<PulseResponse | null>(null);
  const demoRef = useRef(demo);
  answersRef.current = answers;
  responseRef.current = response;
  demoRef.current = demo;

  useEffect(() => { if (user && !profileLoading) load(); /* eslint-disable-next-line */ }, [user, profileLoading, id]);

  async function load() {
    setLoading(true); setErr('');
    const { data: s } = await supabase.from('pulse_surveys').select('*').eq('id', id).maybeSingle();
    if (!s) { setErr('This survey is not available, or you have not been assigned to it.'); setLoading(false); return; }
    const surv = s as PulseSurvey;
    setSurvey(surv);

    const { data: qs } = await supabase.from('pulse_questions').select('*').eq('survey_id', id).order('order_index');
    setQuestions((qs || []).map(normalizeQuestion));

    // get-or-create response
    let { data: resp } = await supabase.from('pulse_responses').select('*').eq('survey_id', id).eq('profile_id', user!.id).maybeSingle();
    if (!resp) {
      const { data: created, error } = await supabase.from('pulse_responses').insert({ survey_id: id, profile_id: user!.id }).select().single();
      if (error) { setErr('Could not start the survey. Please try again.'); setLoading(false); return; }
      resp = created;
    }
    const r = resp as PulseResponse;
    setResponse(r);

    // load existing answers
    const { data: existing } = await supabase.from('pulse_answers').select('*').eq('response_id', r.id);
    const map: AnswerMap = {};
    (existing || []).forEach((a: any) => {
      const q = (qs || []).find((x: any) => x.id === a.question_id);
      if (!q) return;
      map[a.question_id] = valueFromRow(q.question_type, a);
    });
    setAnswers(map);

    // demographics, from response if set, else derive from firm
    const derived = deriveDemographics(firm);
    setDemo({
      strategy: r.firm_strategy || derived.strategy,
      aum: r.firm_aum_range || derived.aum,
      region: r.firm_hq_region || derived.region,
    });

    if (r.is_complete) setSubmitted(true);
    setLoading(false);
  }

  function valueFromRow(type: string, a: any): AnswerValue {
    switch (type) {
      case 'multiple_choice':
      case 'free_text': return { text: a.answer_text || '' };
      case 'rating':
      case 'nps': return { number: a.answer_number };
      case 'multi_select':
      case 'ranking': return { options: Array.isArray(a.answer_options) ? a.answer_options : [] };
      case 'matrix':
      case 'grid': return { matrix: a.answer_matrix || {} };
      default: return {};
    }
  }

  // Flush any dirty answers + demographics.
  const flush = useCallback(async () => {
    const resp = responseRef.current;
    if (!resp) return;
    const ids = Array.from(dirty.current);
    if (!ids.length) return;
    dirty.current.clear();
    setSaving(true);
    const rows = ids.map(qid => {
      const q = questions.find(x => x.id === qid)!;
      return buildPayload(resp.id, q, answersRef.current[qid] || {});
    }).filter(Boolean);
    await supabase.from('pulse_answers').upsert(rows, { onConflict: 'response_id,question_id' });
    await supabase.from('pulse_responses').update({
      firm_strategy: demoRef.current.strategy || null,
      firm_aum_range: demoRef.current.aum || null,
      firm_hq_region: demoRef.current.region || null,
      updated_at: new Date().toISOString(),
    }).eq('id', resp.id);
    setSaving(false);
    setSavedAt(new Date());
  }, [questions]);

  // Auto-save every 30s.
  useEffect(() => {
    const t = setInterval(() => { flush(); }, 30_000);
    return () => { clearInterval(t); };
  }, [flush]);

  // Debounced save on answer change.
  const debounce = useRef<any>(null);
  const onAnswer = (qid: string, v: AnswerValue) => {
    setAnswers(prev => ({ ...prev, [qid]: v }));
    dirty.current.add(qid);
    if (debounce.current) clearTimeout(debounce.current);
    debounce.current = setTimeout(() => flush(), 1200);
  };

  const answered = useMemo(() => questions.filter(q => isAnswered(q, answers)).length, [questions, answers]);
  const requiredMissing = useMemo(() => questions.filter(q => q.required && !isAnswered(q, answers)), [questions, answers]);
  const progress = questions.length ? Math.round((answered / questions.length) * 100) : 0;
  const closed = survey?.status === 'closed';

  async function saveAndExit() {
    // mark every touched question dirty to be safe
    Object.keys(answers).forEach(qid => dirty.current.add(qid));
    await flush();
    navigate('/pulse-connect');
  }

  async function submit() {
    if (requiredMissing.length) { setShowMissing(true); window.scrollTo({ top: 0, behavior: 'smooth' }); return; }
    setSubmitting(true);
    Object.keys(answers).forEach(qid => dirty.current.add(qid));
    await flush();
    await supabase.from('pulse_responses').update({
      is_complete: true, completed_at: new Date().toISOString(), updated_at: new Date().toISOString(),
    }).eq('id', response!.id);
    if (user && survey) logActivity(user.id, firm?.id ?? null, 'survey_completed', 'survey', survey.id, { survey_title: survey.title });
    setSubmitting(false);
    setSubmitted(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  if (loading || profileLoading) return <LoadingScreen label="Loading survey…" />;
  if (err) return <div style={{ padding: '28px 32px', minHeight: '100vh', background: C.surface }}><button onClick={() => navigate('/pulse-connect')} style={{ ...btnGhost, marginBottom: '16px' }}>← Back</button><Banner type="err" text={err} /></div>;
  if (!survey) return null;

  if (submitted) return <ThankYou survey={survey} onResults={() => navigate(`/pulse-connect/results/${survey.id}`)} onDone={() => navigate('/pulse-connect')} />;

  return (
    <div style={{ minHeight: '100vh', background: C.surface, fontFamily: 'system-ui, -apple-system, sans-serif' }}>
      {/* Progress bar */}
      <div style={{ position: 'sticky', top: 0, zIndex: 20, background: C.white, borderBottom: `1px solid ${C.border}`, padding: '12px 32px' }}>
        <div style={{ maxWidth: '760px', margin: '0 auto', display: 'flex', alignItems: 'center', gap: '16px' }}>
          <div style={{ flex: 1 }}>
            <div style={{ height: '6px', background: C.surface, borderRadius: '3px', overflow: 'hidden' }}>
              <div style={{ width: `${progress}%`, height: '100%', background: `linear-gradient(90deg, ${C.navy}, ${C.gold})`, transition: 'width 0.3s' }} />
            </div>
          </div>
          <div style={{ fontSize: '11.5px', color: C.muted, fontWeight: 600, whiteSpace: 'nowrap' }}>{answered} of {questions.length} · {progress}%</div>
          <div style={{ fontSize: '10.5px', color: saving ? C.amber : C.mutedLt, whiteSpace: 'nowrap', minWidth: '78px', textAlign: 'right' }}>
            {saving ? 'Saving…' : savedAt ? `Saved ${savedAt.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}` : ''}
          </div>
        </div>
      </div>

      <div style={{ maxWidth: '760px', margin: '0 auto', padding: '28px 32px 80px' }}>
        <button onClick={() => navigate('/pulse-connect')} style={{ ...btnGhost, marginBottom: '18px' }}>← Exit</button>

        <h1 style={{ fontFamily: 'Georgia, serif', fontSize: '25px', color: C.black, fontWeight: 400, margin: '0 0 8px', letterSpacing: '-0.3px' }}>{survey.title}</h1>
        {survey.description && <p style={{ fontSize: '13.5px', color: C.textSec, lineHeight: 1.6, margin: '0 0 20px' }}>{survey.description}</p>}

        {closed && <Banner type="info" text="This survey is now closed. You can review your answers but can no longer submit." />}
        {showMissing && requiredMissing.length > 0 && <Banner type="err" text={`Please answer all required questions, ${requiredMissing.length} remaining.`} />}

        {/* Demographic section */}
        <div style={{ ...card, padding: '22px 24px', marginBottom: '16px', background: '#FCFCFD' }}>
          <div style={{ fontSize: '16px', fontWeight: 600, color: C.navy, marginBottom: '4px', fontFamily: 'Georgia, serif' }}>Help us understand your perspective</div>
          <div style={{ fontSize: '12.5px', color: C.muted, marginBottom: '18px', lineHeight: 1.5 }}>Pre-filled from your firm profile. Adjust if anything has changed, this is used only to segment aggregate results.</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '14px' }}>
            <div>
              <label style={labelStyle}>Firm Strategy</label>
              <select value={demo.strategy} onChange={e => setDemo(d => ({ ...d, strategy: e.target.value }))} style={inputStyle}>
                <option value="">Select…</option>
                {(demo.strategy && !STRATEGY_OPTIONS.includes(demo.strategy)) && <option value={demo.strategy}>{demo.strategy}</option>}
                {STRATEGY_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
              </select>
            </div>
            <div>
              <label style={labelStyle}>AUM Range</label>
              <select value={demo.aum} onChange={e => setDemo(d => ({ ...d, aum: e.target.value }))} style={inputStyle}>
                <option value="">Select…</option>
                {AUM_RANGES.map(o => <option key={o} value={o}>{o}</option>)}
              </select>
            </div>
            <div>
              <label style={labelStyle}>HQ Region</label>
              <select value={demo.region} onChange={e => setDemo(d => ({ ...d, region: e.target.value }))} style={inputStyle}>
                <option value="">Select…</option>
                {REGION_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
              </select>
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {questions.map((q, i) => {
            const missing = showMissing && q.required && !isAnswered(q, answers);
            return (
              <div key={q.id} style={{ ...card, padding: '22px 24px', border: missing ? `1.5px solid ${C.danger}` : `1px solid ${C.border}` }}>
                <div style={{ display: 'flex', gap: '10px', marginBottom: '16px' }}>
                  <span style={{ fontSize: '12px', fontWeight: 700, color: C.gold, fontFamily: 'Georgia, serif', flexShrink: 0, marginTop: '1px' }}>{i + 1}</span>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: '15px', fontWeight: 600, color: C.text, lineHeight: 1.45 }}>
                      {q.question_text}
                      {q.required && <span style={{ color: C.danger, marginLeft: '4px' }}>*</span>}
                    </div>
                    {q.description && <div style={{ fontSize: '12.5px', color: C.muted, marginTop: '4px', lineHeight: 1.5 }}>{q.description}</div>}
                  </div>
                </div>
                <div style={{ pointerEvents: closed ? 'none' : 'auto', opacity: closed ? 0.7 : 1 }}>
                  <QuestionInput question={q} value={answers[q.id]} onChange={v => onAnswer(q.id, v)} />
                </div>
              </div>
            );
          })}
        </div>

        {/* Actions */}
        <div style={{ display: 'flex', gap: '10px', marginTop: '24px', alignItems: 'center', flexWrap: 'wrap' }}>
          <button onClick={submit} disabled={submitting || closed} style={{ ...btnGold, opacity: submitting || closed ? 0.6 : 1, padding: '11px 26px', fontSize: '13.5px' }}>
            {submitting ? 'Submitting…' : 'Submit Survey'}
          </button>
          <button onClick={saveAndExit} disabled={closed} style={btnGhost}>Save & Continue Later</button>
          {requiredMissing.length > 0 && <span style={{ fontSize: '11.5px', color: C.muted }}>{requiredMissing.length} required question{requiredMissing.length !== 1 ? 's' : ''} remaining</span>}
        </div>
      </div>
    </div>
  );
}

function ThankYou({ survey, onResults, onDone }: { survey: PulseSurvey; onResults: () => void; onDone: () => void }) {
  const canView = survey.show_results_after_completion && survey.results_published;
  return (
    <div style={{ minHeight: '100vh', background: C.surface, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'system-ui, -apple-system, sans-serif', padding: '32px' }}>
      <div style={{ ...card, padding: '48px 44px', maxWidth: '520px', textAlign: 'center' }}>
        <div style={{ width: '58px', height: '58px', borderRadius: '50%', background: 'rgba(10,124,66,0.1)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginBottom: '18px' }}>
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke={C.success} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5" /></svg>
        </div>
        <h1 style={{ fontFamily: 'Georgia, serif', fontSize: '24px', color: C.black, fontWeight: 400, margin: '0 0 8px' }}>Thank you</h1>
        <p style={{ fontSize: '13.5px', color: C.textSec, lineHeight: 1.6, margin: '0 0 24px' }}>
          Your response to <strong>{survey.title}</strong> has been recorded. We appreciate your input.
        </p>
        {canView ? (
          <button onClick={onResults} style={{ ...btnPrimary, marginRight: '10px' }}>View Results</button>
        ) : (
          <div style={{ fontSize: '12.5px', color: C.muted, background: C.surface, borderRadius: '4px', padding: '12px 16px', marginBottom: '20px', lineHeight: 1.5 }}>
            Results will be shared with you once published by the research team.
          </div>
        )}
        <div><button onClick={onDone} style={{ ...btnGhost, marginTop: canView ? 0 : '0' }}>Back to PulseConnect</button></div>
      </div>
    </div>
  );
}
